import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import {
  AbstractControl,
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from '@angular/forms';
import {
  MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA,
  MatLegacyDialogRef as MatDialogRef,
} from '@angular/material/legacy-dialog';
import { MatLegacySelectChange as MatSelectChange } from '@angular/material/legacy-select';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { MatLegacyTabGroup as MatTabGroup } from '@angular/material/legacy-tabs';
import { Observable, Subject, of } from 'rxjs';
import { map, takeUntil, tap } from 'rxjs/operators';

// CONFIGS
import { CountryConfig } from '../../../shared/configs/country.config';
import { SnackBarConfig } from '../../../shared/configs/snackbar.config';
import { StateConfig } from '../../../shared/configs/states.config';

// INTERFACES

// SERVICES
import { ClientCompanyApiService } from '../../../shared/services/api/client/client-company-api.service';
import { ClientPaymentProfileApiService } from '../../../shared/services/api/client/client-payment-profile-api.service';
import { CardUtilService } from '../../../shared/services/util/card-util.service';

// UTILS
import { trackById } from '../../../shared/util/track-by.util';

// VALIDATORS
import { CreditCard } from '../../../shared/validators/credit-card/credit-card';
import { CreditCardValidators } from '../../../shared/validators/credit-card/validators';
import { noWhitespaceValidator } from '../../../shared/validators/no-whitespace-validator';
import { IBusinessAccountContact, ICreditCard } from '../../interfaces';

@Component({
  selector: 'lum-client-payment-profile-form',
  templateUrl: './client-payment-profile-form.component.html',
  styleUrls: ['./client-payment-profile-form.component.scss'],
})
export class ClientPaymentProfileFormComponent implements OnInit, OnDestroy {
  form :UntypedFormGroup;
  card :ICreditCard;
  countries :{ id :string; label :string }[] = [];
  states :{ id :string; label :string }[] = [];
  cardType :string = '';
  loading :boolean = false;

  contact :IBusinessAccountContact;
  contacts :IBusinessAccountContact[] = [];
  contacts$ :Observable<IBusinessAccountContact[]>;
  currentTab :number = 0;

  trackById = trackById;

  private onDestroy$ = new Subject();

  constructor(
    private cardUtilService :CardUtilService,
    private clientPaymentProfileApiService :ClientPaymentProfileApiService,
    private clientCompanyApiService :ClientCompanyApiService,
    @Inject(MAT_DIALOG_DATA) public data :any,
    private dialogRef :MatDialogRef<ClientPaymentProfileFormComponent>,
    private fb :UntypedFormBuilder,
    private snackbar :MatSnackBar,
  ) {}

  ngOnInit() :void {
    this.initCardValue();
    this.initForm();
    this.initCountries();
    this.initStates();
    this.getDataFromService();
    this.getCompanyContacts();
    this.disableFieldsOnEdit();
    this.getContacts().subscribe();
    this.getCardType().subscribe();
  }

  ngOnDestroy() :void {
    this.onDestroy$.next(null);
    this.onDestroy$.complete();
  }

  onClose() :void {
    this.dialogRef.close();
  }

  onChangeTab(tabs :MatTabGroup, index :number) :void {
    tabs.selectedIndex = index;
    this.currentTab = index;
  }

  onSelectContact(event :MatSelectChange) :void {
    this.contact =
      this.contacts.find((contact) => contact.id === event.value) ?? this.contact;

    this.setContactInfo(this.contact);
  }

  onSubmit() :void {
    this.loading = true;

    if (
      (!this.isEdit && this.form.invalid) ||
      (this.isEdit && this.labelControl?.invalid)
    ) {
      return;
    }

    if (!this.isEdit) {
      this.clientPaymentProfileApiService
        .create(
          this.cardUtilService.getFormattedCardInfo({
            card: this.form.value,
            cardType: this.cardType,
            ...this.defaultParams,
          }),
        )
        .pipe(takeUntil(this.onDestroy$))
        .subscribe({
          next: (res :any) => {
            this.dialogRef.close(res);
            this.snackbar.open('Payment profile successfully created.', 'Close', {
              duration: SnackBarConfig.DEFAULT.duration,
            });
          },
          error: (err :any) => {
            const { error } = err;
            this.snackbar.open(error ?? 'Unable to create new card.', 'Close', {
              duration: SnackBarConfig.DEFAULT.duration,
            });
            this.loading = false;
          },
        });
    } else {
      this.clientPaymentProfileApiService
        .updateLabel(this.card.id, {
          ...this.defaultParams,
          userId: this.card.userId,
          label: this.labelControl?.value,
        })
        .pipe(takeUntil(this.onDestroy$))
        .subscribe({
          next: (card :ICreditCard) => {
            this.dialogRef.close(card);
            this.snackbar.open('Payment profile successfully updated.', 'Close', {
              duration: SnackBarConfig.DEFAULT.duration,
            });
          },
          error: (err :any) => {
            const { error } = err;
            this.snackbar.open(error ?? 'Unable to update card.', 'Close', {
              duration: SnackBarConfig.DEFAULT.duration,
            });
            this.loading = false;
          },
        });
    }
  }

  private initForm() :void {
    const {
      userId,
      label,
      firstName,
      lastName,
      address,
      zip,
      city,
      state,
      country,
    } = this.card;

    this.form = this.fb.group({
      number: ['', CreditCardValidators.validateCCNumber],
      expirationDate: ['', CreditCardValidators.validateExpDate],
      cvv: [
        '',
        [Validators.required, Validators.minLength(3), Validators.maxLength(4)],
      ],
      label: [label || '', [Validators.required, noWhitespaceValidator()]],
      clientContactId: [userId || ''],
      firstName: [firstName || '', Validators.required],
      lastName: [lastName || '', Validators.required],
      address: [address || '', Validators.required],
      zip: [zip || '', Validators.required],
      city: [city || '', Validators.required],
      state: [state || '', Validators.required],
      country: [country || '', Validators.required],
    });
  }

  private initCardValue() :void {
    this.card = this.data.creditCard || {};
    this.currentTab = this.isEdit ? 1 : 0;
  }

  private disableFieldsOnEdit() :void {
    if (this.isEdit) {
      this.firstNameControl?.disable();
      this.lastNameControl?.disable();
      this.addressControl?.disable();
      this.countryControl?.disable();
      this.zipControl?.disable();
      this.cityControl?.disable();
      this.stateControl?.disable();
    }
  }

  private initCountries() :void {
    const countries :any = CountryConfig.countries;

    this.countries = Object.keys(countries).map((code :any) => ({
      id: code,
      label: countries[code],
    }));
  }

  private initStates() :void {
    const states :any = StateConfig;

    this.states = Object.keys(states).map((code :any) => ({
      id: code,
      label: states[code],
    }));
  }

  private getDataFromService() :void {
    this.contacts$ = this.clientCompanyApiService.businessAccountsContacts$;
  }

  private getCardType() :Observable<string | null> {
    const numberControl = this.form.get('number');

    if (numberControl) {
      return numberControl.valueChanges.pipe(
        map((num :string) => {
          const type = CreditCard.cardType(num);
          this.cardType = type ?? '';

          return type;
        }),
      );
    } else {
      return of(null);
    }
  }

  private getCompanyContacts() :void {
    this.clientCompanyApiService.getCompanyContacts({
      businessAccountId: this.data.client.id.toString(),
      limit: '1000',
    });
  }

  private setContactInfo(contact :IBusinessAccountContact) :void {
    const { userAccount, businessAccount } = contact;

    this.form.patchValue({
      firstName: userAccount.firstName,
      lastName: userAccount.lastName,
      address: businessAccount.businessAddress,
      zip: businessAccount.businessZip,
      city: businessAccount.businessCity,
      state: businessAccount.businessState,
      country: businessAccount.businessCountry,
    });
  }

  private getContacts() :Observable<IBusinessAccountContact[]> {
    return this.contacts$.pipe(
      tap((contacts) => {
        this.contacts = contacts;
      }),
      takeUntil(this.onDestroy$),
    );
  }

  get defaultParams() :{ businessAccountId :number; userId :number } {
    return {
      businessAccountId: this.contact?.businessAccountId ?? this.data.client.id,
      userId: this.contact?.userAccountId ?? this.data.client.user?.id,
    };
  }

  get numberControl() :AbstractControl | null {
    return this.form.get('number');
  }

  get expirationDateControl() :AbstractControl | null {
    return this.form.get('expirationDate');
  }

  get cvvControl() :AbstractControl | null {
    return this.form.get('cvv');
  }

  get labelControl() :AbstractControl | null {
    return this.form.get('label');
  }

  get firstNameControl() :AbstractControl | null {
    return this.form.get('firstName');
  }

  get lastNameControl() :AbstractControl | null {
    return this.form.get('lastName');
  }

  get addressControl() :AbstractControl | null {
    return this.form.get('address');
  }

  get zipControl() :AbstractControl | null {
    return this.form.get('zip');
  }

  get cityControl() :AbstractControl | null {
    return this.form.get('city');
  }

  get stateControl() :AbstractControl | null {
    return this.form.get('state');
  }

  get countryControl() :AbstractControl | null {
    return this.form.get('country');
  }

  get hasInvalidCardInput() :boolean {
    if (this.isEdit) {
      return this.labelControl?.invalid || false;
    }

    return (
      this.numberControl?.invalid ||
      this.expirationDateControl?.invalid ||
      this.cvvControl?.invalid ||
      this.labelControl?.invalid ||
      false
    );
  }

  get hasInvalidCardProfileInput() :boolean {
    if (this.isEdit) {
      return this.labelControl?.invalid || false;
    }

    return this.form.invalid;
  }

  get isEdit() :boolean {
    return this.data.creditCard !== null;
  }
}
